import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Skeleton, Typography, ToggleButtonGroup, ToggleButton, Tooltip } from "@mui/material";
import { PackageSearch, PackageCheck, Package, List, AlignJustify } from "lucide-react";

import { useDelivery } from "../../../context/DeliveryContext";
import { DeliveryCard } from "./DeliveryCard";
import { LocationModal } from "./LocationModal";
import { DeliveryReview } from "../DeliveryReview";
import { ActionList } from "./ActionList";
import { haversineMeters, getOriginCoords, toDateMaybe } from "./utils";

const CITY_FILTER_RADIUS_M = 50_000;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function DeliveryCardSkeleton() {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="text" width="35%" height={20} />
        <Skeleton variant="text" width={24} height={20} sx={{ mx: 0.5 }} />
        <Skeleton variant="text" width="35%" height={20} />
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Skeleton variant="rounded" width={64} height={22} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rounded" width={56} height={22} sx={{ borderRadius: 1 }} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
        <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: 1.5 }} />
      </Box>
    </Box>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

const EMPTY_CONFIG = {
  active: {
    icon: PackageSearch,
    title: "Ei kuljetuksia saatavilla",
    description: "Uusia kuljetuksia ilmestyy pian. Tarkista uudelleen hetken kuluttua.",
  },
  completed: {
    icon: PackageCheck,
    title: "Ei valmistuneita kuljetuksia",
    description: "Valmiit kuljetuksesi näkyvät tässä.",
  },
  own: {
    icon: Package,
    title: "Ei omia kuljetuksia",
    description: "Kuljetukset jotka olet ottanut näkyvät tässä.",
  },
};

function EmptyState({ status }) {
  const cfg = EMPTY_CONFIG[status] || EMPTY_CONFIG.active;
  const Icon = cfg.icon;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 6, sm: 10 },
        px: 3,
        textAlign: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "18px",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <Icon size={28} strokeWidth={1.5} style={{ opacity: 0.45 }} />
      </Box>
      <Typography variant="subtitle1" fontWeight={700}>{cfg.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
        {cfg.description}
      </Typography>
    </Box>
  );
}

// ─── View toggle ──────────────────────────────────────────────────────────────

function ViewToggle({ view, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        {view === "actions" ? "Tehtävät" : "Kuljetukset"}
      </Typography>
      <ToggleButtonGroup
        value={view}
        exclusive
        onChange={(_, v) => { if (v) onChange(v); }}
        size="small"
        sx={{
          "& .MuiToggleButton-root": {
            px: 1.25,
            py: 0.5,
            border: "1px solid",
            borderColor: "divider",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
          },
        }}
      >
        <Tooltip title="Kortit">
          <ToggleButton value="cards">
            <List size={15} />
          </ToggleButton>
        </Tooltip>
        <Tooltip title="Toimintolista">
          <ToggleButton value="actions">
            <AlignJustify size={15} />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Box>
  );
}

// ─── Area filter helper ───────────────────────────────────────────────────────

function matchesArea(delivery, courierCoords, searchTerm) {
  if (courierCoords) {
    const coords = getOriginCoords(delivery);
    if (coords && haversineMeters(courierCoords, coords) <= CITY_FILTER_RADIUS_M) {
      return true;
    }
  }

  const candidates = [
    delivery?.origin,
    delivery?.destination,
    delivery?.originInfo?.address,
    delivery?.originInfo?.city,
    delivery?.destinationInfo?.address,
    delivery?.destinationInfo?.city,
  ];

  return candidates.some(
    (s) => typeof s === "string" && s.toLowerCase().includes(searchTerm)
  );
}

// ─── DeliveryList ─────────────────────────────────────────────────────────────

export function DeliveryList({
  status,
  sort,
  courierLocation,
  areaFilter,
  transportFilter,
  locationModalOpen,
  onLocationModalClose,
  onLocationConfirm,
  shadowBan,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    getActiveDeliveries,
    getOwnDriveDeliveries,
    getOwnCompletedDeliveries,
    loading,
  } = useDelivery();

  const [reviewOpen, setReviewOpen]         = useState(false);
  const [reviewDelivery, setReviewDelivery] = useState(null);

  const viewParam = searchParams.get("view");
  const view = viewParam === "actions" ? "actions" : "cards";

  const setView = (v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === "cards") next.delete("view");
      else next.set("view", v);
      return next;
    }, { replace: true });
  };

  const courierCoords =
    courierLocation?.coordinates?.lat != null ? courierLocation.coordinates : null;

  const searchTerm = (courierLocation?.city || areaFilter)?.toLowerCase() ?? null;

  const rawData =
    status === "active"
      ? getActiveDeliveries()
      : status === "completed"
      ? getOwnCompletedDeliveries()
      : getOwnDriveDeliveries();

  const raw = shadowBan ? [] : rawData;

  const deliveries = useMemo(() => {
    if (!raw) return [];
    let copy = [...raw];

    // ── Area filter ──────────────────────────────────────────────────────────
    if (areaFilter && searchTerm) {
      copy = copy.filter((d) => matchesArea(d, courierCoords, searchTerm));
    }

    // ── Transport filter ─────────────────────────────────────────────────────
    if (transportFilter) {
      copy = copy.filter((d) => d?.deliveryTransportType === transportFilter);
    }

    // ── Distance sort (secondary) ────────────────────────────────────────────
    if (courierCoords) {
      copy.sort((a, b) => {
        const ca = getOriginCoords(a);
        const cb = getOriginCoords(b);
        if (!ca && !cb) return 0;
        if (!ca) return 1;
        if (!cb) return -1;
        return haversineMeters(courierCoords, ca) - haversineMeters(courierCoords, cb);
      });
    }

    // ── Primary sort ─────────────────────────────────────────────────────────
    if (sort === "urgency") {
      copy.sort((a, b) => {
        const da = toDateMaybe(a?.pickupTime ?? a?.pickUpTime);
        const db = toDateMaybe(b?.pickupTime ?? b?.pickUpTime);
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da - db;
      });
    } else {
      copy.sort((a, b) => (b?.reward ?? 0) - (a?.reward ?? 0));
    }

    return copy;
  }, [raw, sort, courierCoords, areaFilter, searchTerm, transportFilter]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <DeliveryCardSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (!deliveries.length) {
    return <EmptyState status={status} />;
  }

  const canUseActionView = status !== "completed" && status !== "active" && deliveries.length > 0;
  const effectiveView = canUseActionView ? view : "cards";

  return (
    <Box>
      {canUseActionView && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
          <ViewToggle view={effectiveView} onChange={setView} />
        </Box>
      )}

      {effectiveView === "actions" ? (
        <ActionList deliveries={deliveries} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {deliveries.map((delivery) => {
            const id = delivery?.id || delivery?.deliveryId;
            return (
              <DeliveryCard
                key={id}
                delivery={delivery}
                courierCoords={courierCoords}
                onAccept={() => navigate(`/kuljeta-paketti?deliveryId=${id}`)}
                onOpen={()   => navigate(`/kuljeta-paketti?deliveryId=${id}`)}
                onReview={() => { setReviewDelivery(delivery); setReviewOpen(true); }}
              />
            );
          })}
        </Box>
      )}

      <LocationModal
        open={locationModalOpen}
        onClose={onLocationModalClose}
        currentCity={courierLocation?.city}
        onConfirm={onLocationConfirm}
      />

      <DeliveryReview
        delivery={reviewDelivery}
        open={reviewOpen}
        onClose={() => { setReviewOpen(false); setReviewDelivery(null); }}
        onSubmitted={() => {}}
      />
    </Box>
  );
}